import { useState } from 'react'
import { Link as LinkIcon, MapPin, Save } from 'lucide-react'
import type { ChangeEvent } from 'react'
import type { Entity } from '@/lib/types'
import type { components } from '@/lib/client'
import {
  Button,
  Card,
  Combobox,
  FormTip,
  ImageInput,
  Input,
  Row,
  spacing,
} from '@/components/ui'
import { fieldLabelStyle } from '@/components/ui/Input'
import MarkdownEditor from '@/components/LazyMarkdownEditor'
import EntityCard from '@/components/EntityCard'
import { $api } from '@/lib/fetch'
import { searchAddress } from '@/lib/geocode'
import { EventTypeType } from '@/lib/types'

type CreateEntity = components['schemas']['CreateEntityInputBody']

const ALL_TAGS = Object.values(EventTypeType)
/** Entity avatar — square crop. */
const ENTITY_IMAGE_ASPECT = 1

export default function EntityForm({
  label,
  entity,
  onSubmit,
}: {
  label: string
  entity: Entity
  /** Returns a map of field → error message, or void on success. */
  onSubmit: (data: CreateEntity) => Promise<Record<string, string> | void>
}) {
  const { data: kindsData } = $api.useQuery('get', '/api/entity-kinds')
  const kinds = kindsData?.kinds ?? []

  const [form, setForm] = useState({
    name: entity.name,
    description: entity.description,
    kind_id: entity.kind_id ? String(entity.kind_id) : '',
    tags: entity.tags ?? [],
    link: entity.link ?? '',
    location_text: entity.location_text ?? '',
  })
  const [newImage, setNewImage] = useState<{
    base64: string
    type: string
  } | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  const set =
    (k: 'name' | 'link' | 'location_text') =>
    (e: ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }))

  const toggleTag = (tag: string) =>
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tag)
        ? f.tags.filter((t) => t !== tag)
        : [...f.tags, tag],
    }))

  const imagePreview = newImage
    ? `data:${newImage.type};base64,${newImage.base64}`
    : null

  const submit = async () => {
    setLoading(true)
    const payload: CreateEntity = {
      name: form.name,
      description: form.description,
      kind_id: Number(form.kind_id),
      tags: form.tags,
      link: form.link || undefined,
      location_text: form.location_text || undefined,
      new_image: newImage ?? undefined,
    }
    const result = await onSubmit(payload).finally(() => setLoading(false))
    setErrors(result || {})
  }

  // Live preview view-model (re-uses EntityCard).
  const previewEntity: Entity = {
    ...entity,
    name: form.name || 'Nom de l’organisation',
    description: form.description,
    kind_id: Number(form.kind_id) || entity.kind_id,
    tags: form.tags,
    link: form.link || undefined,
    location_text: form.location_text || undefined,
    image_uri: imagePreview ?? entity.image_uri,
  }

  const err = (k: string) =>
    errors[k] ? (
      <span style={{ fontSize: 13, color: 'var(--danger)' }}>{errors[k]}</span>
    ) : null

  return (
    <div
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--space-8) var(--gutter) var(--space-20)',
      }}
    >
      <div className="oumma-overline" style={{ marginBottom: 10 }}>
        Communauté
      </div>
      <h1 style={{ fontSize: 'var(--text-h1)', margin: '0 0 8px' }}>{label}</h1>
      <p
        style={{
          fontSize: 18,
          color: 'var(--ink-2)',
          maxWidth: 580,
          marginBottom: 32,
        }}
      >
        Présentez la mosquée ou l'organisation à la communauté.
      </p>

      <div
        className="oumma-propose-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1.5fr) minmax(300px,1fr)',
          gap: 40,
          alignItems: 'start',
        }}
      >
        <Card padding={28}>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
          >
            <div>
              <Input
                label="Nom"
                placeholder="ex. Grande Mosquée de Paris"
                value={form.name}
                onChange={set('name')}
                required
              />
              {err('name')}
            </div>

            <div>
              <ImageInput
                label="Photo"
                aspect={ENTITY_IMAGE_ASPECT}
                value={entity.image_uri}
                onChange={setNewImage}
                onError={(msg) => setErrors((p) => ({ ...p, new_image: msg }))}
              />
              {err('new_image')}
            </div>

            <div>
              <Combobox
                label="Type"
                options={kinds.map((k) => ({
                  value: String(k.id),
                  label: `${k.emoji} ${k.name}`,
                }))}
                value={form.kind_id}
                onChange={(v) => setForm((f) => ({ ...f, kind_id: v }))}
              />
              {err('kind_id')}
            </div>

            <div>
              <label style={fieldLabelStyle}>Description</label>
              <MarkdownEditor
                value={form.description}
                onChange={(v) => setForm((f) => ({ ...f, description: v }))}
                placeholder="Présentation, activités proposées…"
              />
              {err('description')}
            </div>

            <div>
              <div style={{ ...fieldLabelStyle, marginBottom: spacing.sm }}>
                Tags
              </div>
              <Row gap="sm" style={{ flexWrap: 'wrap' }}>
                {ALL_TAGS.map((tag) => {
                  const active = form.tags.includes(tag)
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 500,
                        fontSize: 13,
                        border: '1px solid var(--border-soft)',
                        background: active
                          ? 'var(--brand-primary)'
                          : 'var(--surface-tint)',
                        color: active ? 'var(--paper-0)' : 'var(--ink-1)',
                        fontFamily: 'var(--font-body)',
                      }}
                    >
                      {tag}
                    </button>
                  )
                })}
              </Row>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
              }}
              className="oumma-set-grid"
            >
              <Combobox
                label="Adresse (facultatif)"
                icon={<MapPin size={18} />}
                freeText
                loadOptions={searchAddress}
                value={form.location_text}
                onChange={(v) => setForm((f) => ({ ...f, location_text: v }))}
              />
              <Input
                label="Lien externe"
                iconLeft={<LinkIcon size={18} />}
                placeholder="https://…"
                value={form.link}
                onChange={set('link')}
              />
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="primary"
                type="submit"
                loading={loading}
                iconLeft={<Save size={18} />}
              >
                Enregistrer
              </Button>
            </div>
          </form>
        </Card>

        <div
          className="oumma-propose-side"
          style={{ position: 'sticky', top: 76 }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
              marginBottom: 12,
            }}
          >
            Aperçu en direct
          </div>
          <EntityCard entity={previewEntity} kinds={kinds} preview />
          <FormTip>
            Voici comment votre organisation apparaîtra. Tout est relu avant
            publication.
          </FormTip>
        </div>
      </div>
    </div>
  )
}
